import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ToggleOn as ToggleOnIcon } from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    roleIds: []
  });
  
  // State for field-level validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch users and roles on component mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Remove duplicate '/api' prefix since it's already in the baseURL
      const response = await apiClient.get('/auth/users');
      setUsers(response.data || []);
    } catch (err) {
      setError('Failed to fetch users. ' + (err.response?.data?.message || err.message));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      // Remove duplicate '/api' prefix since it's already in the baseURL
      const response = await apiClient.get('/auth/roles');
      setRoles(response.data || []);
    } catch (err) {
      setError('Failed to fetch roles. ' + (err.response?.data?.message || err.message));
      setRoles([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleRoleChange = (event) => {
    setFormData({
      ...formData,
      roleIds: event.target.value
    });
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      // Edit mode
      setEditMode(true);
      setSelectedUser(user);
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
        email: user.email,
        phoneNumber: user.phoneNumber || '',
        password: '',
        confirmPassword: '',
        roleIds: user.roles && Array.isArray(user.roles) ? user.roles.map(role => role.roleId) : []
      });
    } else {
      // Create mode
      setEditMode(false);
      setSelectedUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        roleIds: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setSuccess('');
    setFieldErrors({});
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.username || !formData.email || !formData.phoneNumber) {
      setError('Please fill in all required fields');
      return false;
    }

    if (!editMode && (!formData.password || !formData.confirmPassword)) {
      setError('Password is required for new users');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.roleIds.length === 0) {
      setError('Please assign at least one role');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editMode) {
        // Update existing user - using the auth controller endpoint
        await apiClient.put(`/auth/users/${selectedUser.userId}`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username, // Include username in the request even though it can't be changed
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          roleIds: formData.roleIds,
          ...(formData.password ? { password: formData.password } : {}),
          ...(formData.confirmPassword ? { confirmPassword: formData.confirmPassword } : {})
        });
        setSuccess('User updated successfully');
      } else {
        // Create new user - using the register endpoint from AuthController
        await apiClient.post('/auth/register', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          roleIds: formData.roleIds
        });
        setSuccess('User created successfully');
      }
      
      // Refresh user list
      fetchUsers();
      
      // Close dialog after short delay to show success message
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
      
    } catch (err) {
      if (err.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = err.response.data.errors;
        const errorMessages = [];
        const newFieldErrors = {};
        
        // Process each validation error field
        Object.keys(validationErrors).forEach(field => {
          const messages = validationErrors[field];
          if (Array.isArray(messages)) {
            errorMessages.push(`${field}: ${messages.join(', ')}`);
            newFieldErrors[field] = messages.join(', ');
          }
        });
        
        setFieldErrors(newFieldErrors);
        setError(errorMessages.join('\n') || 'Validation errors occurred');
      } else {
        setFieldErrors({});
        setError(err.response?.data?.message || 'An error occurred while saving user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetUserActive = async (userId, isActive) => {
    if (!isActive && !window.confirm('Are you sure you want to deactivate this user?')) return;
    setLoading(true);
    try {
      await apiClient.put(`/auth/users/${userId}/activate`, { isActive });
      setSuccess(isActive ? 'User activated successfully' : 'User deactivated successfully');
      fetchUsers();
    } catch (err) {
      setError(`Failed to ${isActive ? 'activate' : 'deactivate'} user. ` + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Check if current user has admin role
  const isAdmin = currentUser?.roles?.includes('Administrator');

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          You do not have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.roles && Array.isArray(user.roles) ? user.roles.map((role) => (
                      <Chip 
                        key={role.roleId} 
                        label={role.roleName} 
                        size="small" 
                        sx={{ mr: 0.5, mb: 0.5 }} 
                      />
                    )) : null}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Disabled'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                      variant={user.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      color="primary" 
                      onClick={() => handleOpenDialog(user)}
                      aria-label="edit user"
                    >
                      <EditIcon />
                    </IconButton>
                    {user.username !== 'admin' && (
                      user.isActive ? (
                        <IconButton
                          color="error"
                          onClick={() => handleSetUserActive(user.userId, false)}
                          aria-label="deactivate user"
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          color="success"
                          onClick={() => handleSetUserActive(user.userId, true)}
                          aria-label="activate user"
                        >
                          <ToggleOnIcon />
                        </IconButton>
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.FirstName}
                helperText={fieldErrors.FirstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.LastName}
                helperText={fieldErrors.LastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={editMode} // Username cannot be changed once created
                error={!!fieldErrors.Username}
                helperText={fieldErrors.Username}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.Email}
                helperText={fieldErrors.Email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.PhoneNumber}
                helperText={fieldErrors.PhoneNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={editMode ? 'New Password (leave blank to keep current)' : 'Password'}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!editMode}
                error={!!fieldErrors.Password}
                helperText={fieldErrors.Password}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required={!editMode || formData.password !== ''}
                error={!!fieldErrors.ConfirmPassword}
                helperText={fieldErrors.ConfirmPassword}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="roles-label">Roles</InputLabel>
                <Select
                  labelId="roles-label"
                  multiple
                  value={formData.roleIds}
                  onChange={handleRoleChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((roleId) => {
                        const role = roles && roles.find ? roles.find(r => r.roleId === roleId) : null;
                        return role ? (
                          <Chip key={roleId} label={role.roleName} />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {roles && roles.length > 0 ? roles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </MenuItem>
                  )) : <MenuItem disabled>No roles available</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
